import React, { useState } from "react";
import { FiX, FiPrinter, FiShoppingBag, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { toast } from "react-toastify";

const formatPkr = (num) => {
  return Number(num || 0).toLocaleString();
};

const RequisitionDetailModal = ({
  open,
  onClose,
  requisition,
  onDecide,
  onCancelLine,
  onCreatePO,
}) => {
  const [comments, setComments] = useState("");
  const [cancelItemId, setCancelItemId] = useState(null);
  const [cancelQty, setCancelQty] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  if (!open || !requisition) {
    return null;
  }

  const priorityClass =
    requisition.priority === "High / Urgent"
      ? "pill-priority-high"
      : requisition.priority === "Medium"
        ? "pill-priority-medium"
        : "pill-priority-low";

  const statusClass = ["Approved", "Partially Ordered", "Ordered"].includes(
    requisition.approvalStatus,
  )
    ? "pill-status-approved"
    : requisition.approvalStatus === "Pending Approval"
      ? "pill-status-pending"
      : requisition.approvalStatus === "Closed"
        ? "pill-status-completed"
        : "pill-status-rejected";

  const submitLineCancel = (item) => {
    const qty = Number(cancelQty);
    if (!(qty > 0) || qty > item.openQty) {
      toast.error(`Enter a quantity between 0 and ${item.openQty}`);
      return;
    }
    if (!cancelReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    onCancelLine?.(requisition.id, item.id, qty, cancelReason.trim());
    setCancelItemId(null);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="req-modal-overlay" onClick={onClose}>
      <div className="req-modal-box" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="req-modal-header">
          <div className="d-flex align-items-center gap-3">
            <h5 className="req-modal-title m-0">Requisition: {requisition.reqNo}</h5>
            <span className={`pill-badge ${statusClass} ml-2`}>{requisition.approvalStatus}</span>
            <span className={`pill-badge ${priorityClass} ml-1`}>{requisition.priority}</span>
          </div>
          <button type="button" className="req-modal-close" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        {/* Body */}
        <div className="req-modal-body">
          {/* Key metadata grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "14px",
              padding: "14px 16px",
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              marginBottom: "18px",
            }}
          >
            <div>
              <div
                style={{
                  color: "#64748b",
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Project / Site
              </div>
              <div style={{ fontWeight: 600, color: "#0f172a", marginTop: "3px" }}>
                {requisition.project}
              </div>
            </div>
            <div>
              <div
                style={{
                  color: "#64748b",
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Material Requisition
              </div>
              <div style={{ fontWeight: 600, color: "#0f172a", marginTop: "3px" }}>
                {requisition.mrNumber || "-"}
              </div>
            </div>
            <div>
              <div
                style={{
                  color: "#64748b",
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Requested By
              </div>
              <div style={{ fontWeight: 600, color: "#0f172a", marginTop: "3px" }}>
                {requisition.requestedBy}
              </div>
            </div>
            <div>
              <div
                style={{
                  color: "#64748b",
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Date Raised
              </div>
              <div style={{ fontWeight: 600, color: "#0f172a", marginTop: "3px" }}>
                {requisition.dateRaised}
              </div>
            </div>
            <div>
              <div
                style={{
                  color: "#64748b",
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Required By
              </div>
              <div style={{ fontWeight: 600, color: "#0f172a", marginTop: "3px" }}>
                {requisition.requiredBy}
              </div>
            </div>
            <div>
              <div
                style={{
                  color: "#64748b",
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Est. Total Cost
              </div>
              <div
                style={{ fontWeight: 700, color: "#0b6462", marginTop: "3px", fontSize: "14px" }}
              >
                PKR {formatPkr(requisition.estCost)}
              </div>
            </div>
          </div>

          {/* Requested Items Table */}
          <h6 style={{ fontWeight: 700, color: "#1e293b", margin: "14px 0 8px 0" }}>
            Materials / Line Items
          </h6>
          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              overflow: "hidden",
              marginBottom: "16px",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12.5px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f1f5f9", borderBottom: "1px solid #e2e8f0" }}>
                  <th style={{ padding: "8px 12px", textAlign: "left", color: "#475569" }}>#</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", color: "#475569" }}>
                    Material Name
                  </th>
                  <th style={{ padding: "8px 12px", textAlign: "left", color: "#475569" }}>
                    Specification
                  </th>
                  <th style={{ padding: "8px 12px", textAlign: "center", color: "#475569" }}>
                    Qty Required
                  </th>
                  <th style={{ padding: "8px 12px", textAlign: "center", color: "#475569" }}>
                    Open Qty
                  </th>
                  <th style={{ padding: "8px 12px", textAlign: "right", color: "#475569" }}>
                    Est. Rate (PKR)
                  </th>
                  <th style={{ padding: "8px 12px", textAlign: "right", color: "#475569" }}>
                    Total (PKR)
                  </th>
                  {requisition.canCancel && (
                    <th style={{ padding: "8px 12px", textAlign: "center", color: "#475569" }}>
                      Cancel
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(requisition.items || []).map((item, idx) => (
                  <tr key={item.id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "8px 12px" }}>{idx + 1}</td>
                    <td style={{ padding: "8px 12px", fontWeight: 600, color: "#0f172a" }}>
                      {item.materialName}
                    </td>
                    <td style={{ padding: "8px 12px", color: "#64748b" }}>{item.spec || "-"}</td>
                    <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600 }}>
                      {item.qty} {item.uom}
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: 600 }}>
                      {item.openQty} {item.uom}
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#475569" }}>
                      {formatPkr(item.unitPrice)}
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        textAlign: "right",
                        fontWeight: 700,
                        color: "#0b6462",
                      }}
                    >
                      {formatPkr(item.totalCost)}
                    </td>
                    {requisition.canCancel && (
                      <td style={{ padding: "8px 12px", textAlign: "center" }}>
                        {item.openQty > 0 &&
                          (cancelItemId === item.id ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                              <input
                                type="number"
                                className="filter-control-input"
                                style={{ width: "90px" }}
                                placeholder="Qty"
                                value={cancelQty}
                                onChange={(e) => setCancelQty(e.target.value)}
                              />
                              <input
                                type="text"
                                className="filter-control-input"
                                style={{ width: "130px" }}
                                placeholder="Reason"
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                              />
                              <span>
                                <a
                                  href="#cancel-line"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    submitLineCancel(item);
                                  }}
                                >
                                  OK
                                </a>
                                {" | "}
                                <a
                                  href="#cancel-line"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setCancelItemId(null);
                                  }}
                                >
                                  Back
                                </a>
                              </span>
                            </div>
                          ) : (
                            <a
                              href="#cancel-line"
                              style={{ color: "#c0392b" }}
                              onClick={(e) => {
                                e.preventDefault();
                                setCancelItemId(item.id);
                                setCancelQty(String(item.openQty));
                                setCancelReason("");
                              }}
                            >
                              Cancel
                            </a>
                          ))}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(requisition.approvals || []).length > 0 && (
            <>
              <h6 style={{ fontWeight: 700, color: "#1e293b", margin: "14px 0 8px 0" }}>
                Approvers
              </h6>
              <div
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  overflow: "hidden",
                  marginBottom: "16px",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12.5px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f1f5f9", borderBottom: "1px solid #e2e8f0" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", color: "#475569" }}>
                        Approver
                      </th>
                      <th style={{ padding: "8px 12px", textAlign: "center", color: "#475569" }}>
                        Status
                      </th>
                      <th style={{ padding: "8px 12px", textAlign: "left", color: "#475569" }}>
                        Comments
                      </th>
                      <th style={{ padding: "8px 12px", textAlign: "center", color: "#475569" }}>
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {requisition.approvals.map((a) => (
                      <tr key={a.Id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "8px 12px", fontWeight: 600 }}>
                          {a.ApproverName || a.ApproverUserId}
                        </td>
                        <td style={{ padding: "8px 12px", textAlign: "center" }}>{a.Status}</td>
                        <td style={{ padding: "8px 12px", color: "#64748b" }}>
                          {a.Comments || "-"}
                        </td>
                        <td style={{ padding: "8px 12px", textAlign: "center" }}>
                          {a.ActionDate ? String(a.ActionDate).slice(0, 16).replace("T", " ") : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Notes & Justification */}
          {requisition.notes && (
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  color: "#64748b",
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Remarks / Instructions
              </div>
              <div
                style={{
                  marginTop: "4px",
                  padding: "10px 14px",
                  backgroundColor: "#f8fafc",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                  color: "#334155",
                }}
              >
                {requisition.notes}
              </div>
            </div>
          )}

          {requisition.canDecide && (
            <div style={{ marginBottom: "16px" }}>
              <label
                className="filter-label-text"
                style={{ display: "block", marginBottom: "4px" }}
              >
                Comments (optional)
              </label>
              <input
                type="text"
                className="filter-control-input"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>
          )}

          {/* Timeline / History */}
          {requisition.history && requisition.history.length > 0 && (
            <div>
              <div
                style={{
                  color: "#64748b",
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  marginBottom: "8px",
                }}
              >
                Audit Timeline
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {requisition.history.map((h, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "6px 12px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "6px",
                      fontSize: "12px",
                      border: "1px solid #f1f5f9",
                    }}
                  >
                    <span>
                      <strong style={{ color: "#0b6462" }}>{h.step}</strong>
                      {h.by ? ` by ${h.by}` : ""}
                    </span>
                    <span style={{ color: "#94a3b8" }}>{h.date}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="req-modal-footer">
          <button
            type="button"
            className="btn-row-action"
            onClick={handlePrint}
            style={{ height: "36px", padding: "0 16px" }}
          >
            <FiPrinter style={{ marginRight: "6px" }} /> Print
          </button>

          {requisition.canDecide && (
            <button
              type="button"
              className="btn_secondary"
              onClick={() => {
                onDecide?.(requisition.id, "Rejected", comments);
                onClose();
              }}
              style={{ height: "36px", padding: "0 18px", borderRadius: "8px", fontWeight: 600 }}
            >
              <FiXCircle style={{ marginRight: "6px" }} /> Reject
            </button>
          )}

          {requisition.canDecide && (
            <button
              type="button"
              className="btn_primary"
              onClick={() => {
                onDecide?.(requisition.id, "Approved", comments);
                onClose();
              }}
              style={{ height: "36px", padding: "0 18px", borderRadius: "8px", fontWeight: 600 }}
            >
              <FiCheckCircle style={{ marginRight: "6px" }} /> Approve Requisition
            </button>
          )}

          {["Approved", "Partially Ordered"].includes(requisition.approvalStatus) &&
            (requisition.items || []).some((i) => i.openQty > 0) && (
              <button
                type="button"
                className="btn_primary"
                onClick={() => {
                  if (onCreatePO) {
                    onCreatePO(requisition);
                  }
                  onClose();
                }}
                style={{ height: "36px", padding: "0 18px", borderRadius: "8px", fontWeight: 600 }}
              >
                <FiShoppingBag style={{ marginRight: "6px" }} /> Create Purchase Order
              </button>
            )}

          <button
            type="button"
            className="btn_secondary"
            onClick={onClose}
            style={{ height: "36px", padding: "0 18px", borderRadius: "8px", fontWeight: 600 }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequisitionDetailModal;
